import { clsx, type ClassValue } from "clsx";
import { z } from "zod";
import { twMerge } from "tailwind-merge";
import type { JsonValue } from "~/types/database";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalize a stored URL (LinkedIn, website, etc.) into a safe absolute href.
 * Many stored values lack a scheme (e.g. "linkedin.com/in/foo"), which makes a
 * raw href or window.open() resolve against the app domain instead of the real
 * site. Prefix https:// when no scheme is present. Returns undefined for empty.
 */
export function externalHref(value?: string | null): string | undefined {
  const v = (value ?? "").trim();

  if (!v) return undefined;

  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

/**
 * Human-readable file size ("512 B", "1.5 KB", "3.0 MB"). Zero/nullish sizes
 * return `empty`, which call sites set to whatever their layout shows for "no
 * size" ("0 B" by default; e.g. "" or "Unknown").
 */
export function formatFileSize(bytes: number | null | undefined, empty = "0 B"): string {
  if (!bytes) return empty;

  if (bytes < 1024) return `${bytes} B`;

  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Anything `coerceString` is asked to render: a JSON value as it came out of a jsonb
 * column or an API response, a `bigint` as the Postgres driver returns one, or
 * nothing at all. Named rather than left as `unknown` because it is the whole set of
 * things a lead field, a query result cell or a parsed payload can hold.
 */
export type RenderableValue = JsonValue | bigint | undefined;

/** A renderable value that is already a single token of text, or reads as one. */
const RenderableText = z
  .union([z.string(), z.number(), z.boolean(), z.bigint()])
  .transform((token) => String(token));

/**
 * Coerce a value to a string, JSON-encoding objects and arrays so they don't
 * silently collapse to "[object Object]". Nullish becomes an empty string. Use for
 * values whose type is wider than a primitive (parsed JSON, DB rows) but which are
 * expected to render as text.
 */
export function coerceString(value: RenderableValue): string {
  if (value == null) return "";
  const token = RenderableText.safeParse(value);

  if (token.success) return token.data;

  // Objects and arrays: JSON-encode instead of "[object Object]".
  // Same lib.d.ts gap as cron/dispatcher.server: `stringify` is typed `string` but
  // answers `undefined` for undefined/function/symbol inputs.
  // eslint-disable-next-line typescript/no-unnecessary-condition
  return JSON.stringify(value) ?? "";
}
